import {
  getBrowserDefaultLanguage,
  getBrowserDefaultLocaleKey,
} from "@faire/retailer-visitor-shared/consts/LANGUAGE";
import { useUser } from "@faire/retailer-visitor-shared/hooks/user/useUser";
import { getLocaleKey } from "@faire/retailer-visitor-shared/serialized-data/getLocaleKey";
import { DEFAULT_LOCALE_KEY } from "@faire/web--source/common/consts/DEFAULT_LOCALE";
import { SHOP_PREVIEW_OVERRIDE_KEY } from "@faire/web--source/common/consts/SHOP_PREVIEW_OVERRIDE_KEY";
import { localeStringToLocaleKey } from "@faire/web--source/common/consts/TWO_TO_THREE_LETTER_LANGUAGE_CODE";
import { getCookies } from "@faire/web--source/common/cookies";
import type { Language } from "@faire/web-api--source/indigofair/languages/Language";
import type { ILocaleKey } from "@faire/web-api--source/indigofair/locale/ILocaleKey";

export const useUserLocale = () => {
  const { user } = useUser();
  // userLocale is used for preferred communication language
  const userLocale: keyof typeof Language =
    user?.locale_key?.language ??
    getBrowserDefaultLanguage() ??
    DEFAULT_LOCALE_KEY.language;

  // browserLevelLanguage is only for browsing purpose. It doesn't save language in database
  const browserLevelLanguage: keyof typeof Language =
    getLocaleKey()?.language ?? userLocale;

  const userLocaleKey: ILocaleKey =
    user?.locale_key ?? getBrowserDefaultLocaleKey() ?? DEFAULT_LOCALE_KEY;

  const browserLevelLocaleKey: ILocaleKey = getLocaleKey() ?? userLocaleKey;

  const browserLevelLocaleOverride: ILocaleKey | undefined = (() => {
    const overrideLanguage = getCookies(SHOP_PREVIEW_OVERRIDE_KEY);
    if (
      !overrideLanguage ||
      !localeStringToLocaleKey(overrideLanguage).language
    ) {
      return undefined;
    }
    return localeStringToLocaleKey(overrideLanguage);
  })();

  return {
    userLocale,
    browserLevelLanguage,
    userLocaleKey,
    browserLevelLocaleKey,
    browserLevelLocaleOverride,
  };
};
